import { supabase } from "./supabase";
import { logger } from "./logger";

interface EmailOptions {
  to: string;
  subject?: string;
  html?: string;
  text?: string;
  from?: {
    email: string;
    name: string;
  };
  replyTo?: string;
}

interface RetryOptions {
  maxAttempts?: number;
  initialDelay?: number;
  maxDelay?: number;
}

const DEFAULT_RETRY_OPTIONS: RetryOptions = {
  maxAttempts: 3,
  initialDelay: 1000, // 1 second
  maxDelay: 5000, // 5 seconds
};

async function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function retryWithExponentialBackoff<T>(
  operation: () => Promise<T>,
  options: RetryOptions = DEFAULT_RETRY_OPTIONS,
): Promise<T> {
  const { maxAttempts = 3, initialDelay = 1000, maxDelay = 5000 } = options;
  let lastError: Error | undefined;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error as Error;
      if (attempt === maxAttempts) break;

      const delay = Math.min(initialDelay * Math.pow(2, attempt - 1), maxDelay);
      logger.warn(
        `Attempt ${attempt} failed, retrying in ${delay}ms...`,
        error,
      );
      await wait(delay);
    }
  }

  throw lastError;
}

/**
 * Send an email using the Supabase Edge Function
 */
export async function sendEmail(
  options: EmailOptions,
): Promise<{ success: boolean; messageId?: string; error?: string }> {
  // Log the email attempt for debugging
  logger.log("Attempting to send email:", {
    to: options.to.substring(0, 3) + "***", // Mask email for privacy
    subject: options.subject,
    hasHtml: !!options.html,
    hasText: !!options.text,
    isDev: import.meta.env.DEV,
    mode: import.meta.env.MODE,
    environment: import.meta.env.VITE_APP_ENV || "unknown",
  });

  // Skip actual email sending in development mode
  if (import.meta.env.DEV) {
    logger.log("Email sending skipped in development mode:", {
      to: options.to.substring(0, 3) + "***", // Mask email for privacy
      subject: options.subject,
    });
    return {
      success: true,
      messageId: "dev-mode-" + crypto.randomUUID(),
    };
  }

  logger.log("Production email sending - checking Supabase auth session...");

  try {
    const sendEmailOperation = async () => {
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      logger.log("Session check:", {
        hasSession: !!session,
        hasAccessToken: !!session?.access_token,
        userEmail: session?.user?.email?.substring(0, 3) + "***" || "none",
        sessionError: sessionError?.message,
      });

      // For welcome emails during signup, we might not have a session yet
      // Try to get a fresh session or use service role key approach
      let authToken = session?.access_token;

      if (!authToken) {
        logger.log("No session found, attempting to refresh...");
        const { data: refreshResult } = await supabase.auth.refreshSession();
        if (refreshResult.session) {
          authToken = refreshResult.session.access_token;
          logger.log("Session refreshed successfully");
        } else {
          logger.log("No session available, using anonymous access");
        }
      }

      const { data, error } = await supabase.functions.invoke("send-email", {
        body: {
          ...options,
          from: options.from || {
            email: "admin@sermonbuild.com",
            name: "SermonBuild",
          },
        },
        headers: authToken
          ? {
              Authorization: `Bearer ${authToken}`,
            }
          : {
              "X-Client-Info": "anonymous-email",
            },
      });

      logger.log("Supabase function response:", {
        hasData: !!data,
        hasError: !!error,
      });

      if (error) {
        logger.error("Email sending error:", {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code,
        });

        // Check if it's a temporary service issue (like rate/quota limits)
        if (
          error.message?.includes("temporarily unavailable") ||
          error.message?.includes("credit limit") ||
          error.message?.includes("limit reached")
        ) {
          logger.warn(
            "📧 Email service temporarily unavailable due to limits",
          );
          throw new Error(
            "Email service temporarily unavailable. Please try again later.",
          );
        }

        throw new Error(error.message || "Failed to send email");
      }

      return {
        success: true,
        messageId: data?.messageId || crypto.randomUUID(),
      };
    };

    const result = await retryWithExponentialBackoff(sendEmailOperation);
    logger.log("Email sent successfully:", result);

    return result;
  } catch (error) {
    logger.error("Email client error:", {
      error: {
        name: error?.constructor?.name,
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      },
      options: {
        ...options,
        to: options.to.substring(0, 3) + "***", // Log partial email for privacy
      },
    });

    return {
      success: false,
      error: error instanceof Error ? error.message : "Failed to send email",
    };
  }
}

/**
 * Send a welcome email to a new user
 */
export async function sendWelcomeEmail(
  email: string,
  firstName: string,
  selectedPlan: string = "Basic",
): Promise<{ success: boolean; error?: string }> {
  try {
    logger.log(
      `Sending welcome email to ${email.substring(0, 3)}*** with plan ${selectedPlan}`,
    );
    logger.log("Environment check:", {
      isDev: import.meta.env.DEV,
      mode: import.meta.env.MODE,
      appEnv: import.meta.env.VITE_APP_ENV,
      supabaseUrl: import.meta.env.VITE_SUPABASE_URL?.substring(0, 20) + "***",
    });

    // In development, just log and return success
    if (import.meta.env.DEV) {
      logger.log("Welcome email would be sent (dev mode):", {
        to: email,
        firstName,
        selectedPlan,
      });
      return { success: true };
    }

    logger.log("Production mode - attempting to send welcome email...");

    const result = await sendEmail({
      to: email,
      subject: "Welcome to SermonBuild!",
      html: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
          <div style="text-align: center; margin-bottom: 30px;">
            <img src="http://sermonbuild.com/wp-content/uploads/2025/03/logo.png" alt="SermonBuild" style="max-width: 200px; height: auto;">
          </div>
          <h1 style="color: #2874A6; text-align: center;">Welcome to SermonBuild, ${firstName}!</h1>
          <p>Thank you for signing up for SermonBuild ${selectedPlan}. We're excited to help you create powerful, impactful sermons.</p>
          <p>Your 14-day free trial has begun. During this time, you'll have full access to all our premium features.</p>
          
          <div style="text-align: center; margin: 30px 0;">
            <a href="${import.meta.env.VITE_APP_URL || "https://app.sermonbuild.com"}/auth" 
               style="display: inline-block; padding: 14px 28px; background-color: #2874A6; color: white; text-decoration: none; border-radius: 6px; font-weight: 600;">
              Login to Your Account
            </a>
          </div>
          
          <h2 style="color: #2874A6;">Getting Started</h2>
          <ol>
            <li>Complete your profile</li>
            <li>Create your first sermon</li>
            <li>Try our AI analysis tools</li>
          </ol>
          
          <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #F2E8D5; text-align: center;">
            <p>Need help getting started? Visit our <a href="https://support.sermonbuild.com" style="color: #2874A6; text-decoration: none;">Support Center</a> or contact our support team.</p>
            <p style="font-size: 12px; color: #666; margin-top: 20px;">
              SermonBuild - Modern Tools for Powerful Preaching<br>
              Questions? Email us at <a href="mailto:support@sermonbuild.com" style="color: #2874A6;">support@sermonbuild.com</a>
            </p>
          </div>
        </div>
      `,
      from: {
        email: "admin@sermonbuild.com",
        name: "SermonBuild",
      },
    });

    logger.log("Welcome email send result:", result);
    return result;
  } catch (error) {
    logger.error("Welcome email error:", error);
    return {
      success: false,
      error:
        error instanceof Error ? error.message : "Failed to send welcome email",
    };
  }
}

/**
 * Send a password reset email
 */
export async function sendPasswordResetEmail(
  email: string,
  firstName: string,
  resetToken: string,
): Promise<{ success: boolean; error?: string }> {
  try {
    const resetUrl = `${import.meta.env.VITE_APP_URL || "https://app.sermonbuild.com"}/auth/reset-password?token=${resetToken}`;

    return await sendEmail({
      to: email,
      subject: "Reset Your SermonBuild Password",
      html: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h1 style="color: #2874A6;">Reset Your Password</h1>
          <p>Hello ${firstName},</p>
          <p>We received a request to reset your SermonBuild password. Click the button below to set a new password:</p>
          <div style="text-align: center; margin: 30px 0;">
            <a href="${resetUrl}" style="background-color: #2874A6; color: white; padding: 12px 24px; text-decoration: none; border-radius: 4px; font-weight: bold;">Reset Password</a>
          </div>
          <p>If you didn't request a password reset, you can safely ignore this email.</p>
          <p>This link will expire in 24 hours.</p>
          <p>Best regards,<br>The SermonBuild Team</p>
        </div>
      `,
      from: {
        email: "admin@sermonbuild.com",
        name: "SermonBuild",
      },
    });
  } catch (error) {
    logger.error("Password reset email error:", error);
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : "Failed to send password reset email",
    };
  }
}

/**
 * Send a trial ending notification email
 */
export async function sendTrialEndingEmail(
  email: string,
  firstName: string,
  daysLeft: number,
  planName: string,
  billingInterval: string,
  amount: number,
): Promise<{ success: boolean; error?: string }> {
  try {
    return await sendEmail({
      to: email,
      subject: "Your SermonBuild trial ends soon",
      html: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h1 style="color: #2874A6;">Your Trial Ends Soon</h1>
          <p>Hello ${firstName},</p>
          <p>Your free trial of SermonBuild will end in <strong>${daysLeft} days</strong>.</p>
          <p>To continue using all premium features, your selected plan (${planName} - ${billingInterval}) will begin at $${amount} per ${billingInterval} after your trial ends.</p>
          <p>To manage your subscription or change plans, visit your <a href="${import.meta.env.VITE_APP_URL || "https://app.sermonbuild.com"}/dashboard/settings" style="color: #2874A6;">account settings</a>.</p>
          <p>Best regards,<br>The SermonBuild Team</p>
        </div>
      `,
      from: {
        email: "admin@sermonbuild.com",
        name: "SermonBuild",
      },
    });
  } catch (error) {
    logger.error("Trial ending email error:", error);
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : "Failed to send trial ending notification",
    };
  }
}

/**
 * Send a subscription confirmation email
 */
export async function sendSubscriptionConfirmationEmail(
  email: string,
  firstName: string,
  planName: string,
  billingInterval: string,
  nextPaymentDate: string,
): Promise<{ success: boolean; error?: string }> {
  try {
    return await sendEmail({
      to: email,
      subject: "Welcome to SermonBuild Premium!",
      html: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h1 style="color: #2874A6;">Welcome to SermonBuild Premium!</h1>
          <p>Hello ${firstName},</p>
          <p>Thank you for subscribing to SermonBuild Premium! Your subscription is now active and you have access to all premium features.</p>
          <h2 style="color: #2874A6;">Subscription Details</h2>
          <ul>
            <li>Plan: ${planName}</li>
            <li>Billing Cycle: ${billingInterval}</li>
            <li>Next Payment: ${nextPaymentDate}</li>
          </ul>
          <h2 style="color: #2874A6;">Getting Started</h2>
          <ol>
            <li>Explore our advanced sermon editor</li>
            <li>Try the AI-powered sermon analysis</li>
            <li>Set up your sermon schedule</li>
          </ol>
          <p>If you have any questions, please don't hesitate to reach out to our support team.</p>
          <p>Best regards,<br>The SermonBuild Team</p>
        </div>
      `,
      from: {
        email: "admin@sermonbuild.com",
        name: "SermonBuild",
      },
    });
  } catch (error) {
    logger.error("Subscription confirmation email error:", error);
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : "Failed to send subscription confirmation",
    };
  }
}

/**
 * Send user feedback / feature request / bug report to admin
 */
export async function sendFeedbackEmail({
  type,
  subject,
  message,
  userEmail,
}: {
  type: string;
  subject: string;
  message: string;
  userEmail: string;
}): Promise<{ success: boolean; error?: string }> {
  try {
    return await sendEmail({
      to: "admin@sermonbuild.com",
      subject: `[${type}] ${subject}`,
      from: { email: "admin@sermonbuild.com", name: "SermonBuild Feedback" },
      html: `
        <div style="font-family: sans-serif; max-width: 600px; margin: 0 auto; padding: 24px;">
          <h2 style="color: #1a1a2e; margin-bottom: 4px;">${type}</h2>
          <p style="color: #666; font-size: 13px; margin-top: 0; margin-bottom: 24px;">
            From: <strong>${userEmail}</strong>
          </p>
          <h3 style="color: #1a1a2e; margin-bottom: 8px;">${subject}</h3>
          <div style="background: #f8f8f8; border-left: 4px solid #4f46e5; padding: 16px; border-radius: 4px; white-space: pre-wrap; color: #333; font-size: 15px; line-height: 1.6;">
            ${message.replace(/</g, '&lt;').replace(/>/g, '&gt;')}
          </div>
        </div>
      `,
      text: `[${type}] from ${userEmail}\n\n${subject}\n\n${message}`,
    });
  } catch (error) {
    logger.error("Feedback email error:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Failed to send feedback",
    };
  }
}

/**
 * Disable Supabase email sending for auth flows
 * This is a workaround to prevent Supabase from sending its own emails
 */
export async function disableSupabaseEmails(): Promise<void> {
  try {
    // This is a placeholder function - in a real implementation,
    // you would need to use Supabase's admin API to configure email settings
    logger.log("Disabling Supabase emails (placeholder function)");

    // In production, you would use the Supabase Management API to update email settings
    // This requires admin access and is typically done during deployment
  } catch (error) {
    logger.error("Error disabling Supabase emails:", error);
  }
}
